type SectionSwitchNotificationOptions = {
    currentCountry: CountryEntryStoryblok;
    previousCountry: CountryEntryStoryblok | null;

    onConfirm?: () => void | Promise<void>;
};

type SectionSwitchNotificationState = {
    isVisible: boolean;
    mountId: string;

    currentCountry: CountryEntryStoryblok | null;
    previousCountry: CountryEntryStoryblok | null;
};

let hideCurrentToast: (() => void) | null = null;
let confirmCurrentToast: (() => void | Promise<void>) | null = null;
let toastCounter = 0;

export const useSwitchNotification = () => {
    const toast = useToast();
    const state = useState<SectionSwitchNotificationState>('section-switch-notification', () => ({
        isVisible: false,
        mountId: '',
        currentCountry: null,
        previousCountry: null
    }));

    const reset = () => {
        state.value = {
            isVisible: false,
            mountId: '',
            currentCountry: null,
            previousCountry: null
        };

        hideCurrentToast = null;
        confirmCurrentToast = null;
    };
    const closeSectionSwitchNotification = () => {
        hideCurrentToast?.();
    };

    const confirmSectionSwitchNotification = async () => {
        const confirm = confirmCurrentToast;

        closeSectionSwitchNotification();

        await confirm?.();
    };

    const showSectionSwitchNotification = (options: SectionSwitchNotificationOptions) => {
        if (import.meta.server) return;

        hideCurrentToast?.();

        toastCounter += 1;
        const mountId = `section-switch-toast-${toastCounter}`;
        const timeoutDuration = 5500;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let timeoutStartedAt = 0;
        let remainingTimeout = timeoutDuration;
        let progressIntervalId: ReturnType<typeof setInterval> | null = null;
        let progressElement: HTMLDivElement | null = null;
        const pauseReasons = new Set<'focus' | 'hover'>();

        const startTimeout = (toastElement: HTMLDivElement) => {
            if (toastElement.matches(':hover')) pauseReasons.add('hover');
            if (timeoutId || remainingTimeout <= 0 || pauseReasons.size > 0) return;

            timeoutStartedAt = Date.now();
            timeoutId = setTimeout(() => {
                timeoutId = null;
                if (progressIntervalId) clearInterval(progressIntervalId);
                progressIntervalId = null;
                progressElement?.style.setProperty('--toast-progress', '0');
                toast.hide({ transitionOut: 'fadeOut' }, toastElement, 'button');
            }, remainingTimeout);

            progressIntervalId = setInterval(() => {
                const remaining = Math.max(0, remainingTimeout - (Date.now() - timeoutStartedAt));
                progressElement?.style.setProperty('--toast-progress', `${remaining / timeoutDuration}`);
            }, 50);
        };

        const pauseTimeout = (reason: 'focus' | 'hover') => {
            if (pauseReasons.has(reason)) return;
            pauseReasons.add(reason);
            if (!timeoutId) return;

            clearTimeout(timeoutId);
            timeoutId = null;
            remainingTimeout = Math.max(0, remainingTimeout - (Date.now() - timeoutStartedAt));
            progressElement?.style.setProperty('--toast-progress', `${remainingTimeout / timeoutDuration}`);
            if (progressIntervalId) clearInterval(progressIntervalId);
            progressIntervalId = null;
        };

        const resumeTimeout = (reason: 'focus' | 'hover', toastElement: HTMLDivElement) => {
            pauseReasons.delete(reason);
            startTimeout(toastElement);
        };

        toast.show({
            message: `
                <div
                    id="${mountId}"
                    class="language-switch-toast"
                ></div>
            `,
            position: 'topRight',
            timeout: false,
            progressBar: false,
            progressBarColor: 'var(--brand-primary)',
            class: 'switch-section-notification',
            close: false,

            onOpening(_settings, toastElement) {
                toastElement.setAttribute('role', 'status');
                toastElement.setAttribute('aria-live', 'polite');
                toastElement.setAttribute('aria-atomic', 'true');

                progressElement = document.createElement('div');
                progressElement.className = 'section-switch-toast-progress';
                progressElement.setAttribute('aria-hidden', 'true');
                progressElement.style.setProperty('--toast-progress', '1');
                toastElement.append(progressElement);

                toastElement.addEventListener('focusin', () => pauseTimeout('focus'));
                toastElement.addEventListener('focusout', (event) => {
                    const nextTarget = event.relatedTarget;
                    if (!(nextTarget instanceof Node) || !toastElement.contains(nextTarget)) {
                        resumeTimeout('focus', toastElement);
                    }
                });
                toastElement.addEventListener('mouseenter', () => pauseTimeout('hover'));
                toastElement.addEventListener('mouseleave', () => resumeTimeout('hover', toastElement));

                hideCurrentToast = () => {
                    toast.hide(
                        {
                            transitionOut: 'fadeOut'
                        },
                        toastElement,
                        'button'
                    );
                };

                confirmCurrentToast = options.onConfirm ?? null;

                state.value = {
                    mountId,
                    isVisible: true,
                    currentCountry: options.currentCountry,
                    previousCountry: options.previousCountry
                };

                startTimeout(toastElement);
            },

            onOpened(_settings, toastElement) {
                if (toastElement.matches(':hover')) pauseReasons.add('hover');
                startTimeout(toastElement);
            },

            onClosed() {
                if (timeoutId) clearTimeout(timeoutId);
                if (progressIntervalId) clearInterval(progressIntervalId);
                timeoutId = null;
                progressIntervalId = null;
                progressElement = null;

                if (state.value.mountId === mountId) {
                    reset();
                }
            }
        });
    };

    return {
        state,
        showSectionSwitchNotification,
        closeSectionSwitchNotification,
        confirmSectionSwitchNotification
    };
};
